import { useMemo, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import PublicShell from "@/components/PublicShell";
import { useCart, effectiveUnit, lineTotal, tierFor } from "@/context/CartContext";
import { PREFILL_KEY } from "@/pages/BusinessReorderPage";
import { api, fileUrl, imgUrl } from "@/lib/api";
import { useMoney } from "@/context/CurrencyContext";
import { Trash2, Minus, Plus, Truck } from "lucide-react";

const COUNTRIES = [
  ["US", "United States"], ["CA", "Canada"], ["GB", "United Kingdom"],
  ["AU", "Australia"], ["DE", "Germany"], ["FR", "France"], ["ES", "Spain"],
  ["IT", "Italy"], ["NL", "Netherlands"], ["SE", "Sweden"], ["NO", "Norway"],
  ["DK", "Denmark"], ["FI", "Finland"], ["IE", "Ireland"], ["NZ", "New Zealand"],
];

// Social attribution captured on landing (PublicShell); kept for 30 days
const readUtm = () => {
  try {
    const u = JSON.parse(localStorage.getItem("fabj_utm") || "null");
    if (!u || Date.now() - u.at > 30 * 86400000) return null;
    const { at, ...rest } = u;
    return rest;
  } catch { return null; }
};

export default function CartPage() {
  const { items, remove, setQty, clear, total: subtotal } = useCart();
  const money = useMoney();
  const [addr, setAddr] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    address_line1: "", address_line2: "", city: "", state: "",
    postal_code: "", country: "US",
  });
  useEffect(() => {
    // One-click reorder from a confirmation email pre-fills contact details (new address is typed fresh)
    try {
      const raw = sessionStorage.getItem(PREFILL_KEY);
      if (raw) { setAddr(a => ({ ...a, ...JSON.parse(raw) })); sessionStorage.removeItem(PREFILL_KEY); }
    } catch { /* ignore */ }
  }, []);
  const [quote, setQuote] = useState(null); // {shipping_amount, currency, method_uid, min_days, max_days}
  const [quoting, setQuoting] = useState(false);
  const [checking, setChecking] = useState(false);

  const hasPrints = useMemo(() => items.some(i => i.prints_only), [items]);
  const addressComplete = addr.first_name && addr.last_name && addr.email &&
    addr.address_line1 && addr.city && addr.postal_code && addr.country;

  const shippingAmount = quote?.shipping_amount || 0;
  const grandTotal = subtotal + shippingAmount;

  const getQuote = async () => {
    if (!addressComplete) { toast.error("Please complete shipping address"); return; }
    setQuoting(true);
    try {
      const payload = {
        items: items.map(i => ({ artwork_id: i.artwork_id, collection_id: i.collection_id || null, size: i.size, quantity: i.quantity, frame_color: i.frame_color || null, material: i.material || "poster" })),
        address: addr,
      };
      const { data } = await api.post("/prints/shipping-quote", payload);
      setQuote(data);
      if (data.note) toast.info(data.note);
      else toast.success(`Shipping calculated: ${money(data.shipping_amount)}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not get quote");
    } finally { setQuoting(false); }
  };

  const checkout = async () => {
    if (items.length === 0) return;
    if (!addressComplete) { toast.error("Please complete shipping address"); return; }
    if (hasPrints && !quote) { toast.error("Please calculate shipping first"); return; }
    setChecking(true);
    try {
      const payload = {
        items: items.map(i => ({ artwork_id: i.artwork_id, collection_id: i.collection_id || null, size: i.size, quantity: i.quantity, frame_color: i.frame_color || null, material: i.material || "poster" })),
        origin_url: window.location.origin,
        utm: readUtm(),
        address: addr,
        shipping_amount: quote?.shipping_amount || 0,
        shipment_method_uid: quote?.shipment_method_uid || "",
      };
      const { data } = await api.post("/checkout-cart-v2", payload);
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
      setChecking(false);
    }
  };

  return (
    <PublicShell>
      <section className="max-w-6xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <div className="overline text-mute mb-4">Your Cart</div>
        <h1 className="font-serif text-5xl md:text-6xl font-light mb-12 text-ink">
          {items.length === 0 ? "The cart is empty." : `${items.length} piece${items.length > 1 ? "s" : ""}, chosen with care.`}
        </h1>

        {items.length === 0 ? (
          <div>
            <p className="text-ink-soft mb-8">Wander the gallery — every piece has a story.</p>
            <Link to="/gallery" className="btn-ink" data-testid="cart-browse-gallery">Browse Gallery</Link>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2 space-y-6">
              {items.map(i => (
                <div key={i.artwork_id + i.size + (i.frame_color || "") + (i.material || "poster")} className="flex gap-6 border-b border-ink-10 pb-6" data-testid={`cart-item-${i.artwork_id}-${i.size}-${i.frame_color || "unframed"}-${i.material || "poster"}`}>
                  <Link to={`/art/${i.artwork_id}`} className="w-32 h-32 bg-cream-soft flex-shrink-0 art-image-wrap">
                    {i.image && <img src={imgUrl(fileUrl(i.image), 320)} alt={i.title} loading="lazy" decoding="async" className="w-full h-full object-cover" />}
                  </Link>
                  <div className="flex-1 min-w-0">
                    <Link to={`/art/${i.artwork_id}`} className="font-serif text-2xl leading-tight hover:text-accent text-ink">{i.title}</Link>
                    <div className="text-sm text-mute mt-1">
                      {i.prints_only ? "Print · " : "Original · "}Size: {i.size}
                      {i.material && i.material !== "poster" && (
                        <> · Material: <span className="text-ink">{i.material_label || i.material}</span></>
                      )}
                      {i.frame_color && (
                        <> · Frame: <span className="text-ink">{({ "natural-wood": "Natural Wood", "dark-wood": "Dark Wood" })[i.frame_color] || i.frame_color.charAt(0).toUpperCase() + i.frame_color.slice(1)}</span></>
                      )}
                    </div>
                    <div className="text-sm text-ink-soft mt-1">
                      {money(effectiveUnit(i))} {i.collection_id ? "per set" : "each"}
                      {tierFor(i) && <span className="ml-2 text-[11px] uppercase tracking-widest text-accent" data-testid={`tier-badge-${i.artwork_id}`}>{tierFor(i).percent_off}% volume discount</span>}
                    </div>
                    {i.collection_id && !tierFor(i) && (i.volume_tiers || []).length > 0 && (
                      <div className="text-xs text-mute mt-1" data-testid={`tier-hint-${i.artwork_id}`}>Order {i.volume_tiers[0].min_qty}+ sets for {i.volume_tiers[0].percent_off}% off</div>
                    )}
                    <div className="mt-4 flex items-center gap-4">
                      {i.prints_only && (
                        <div className="inline-flex items-center gap-2">
                          <button onClick={() => setQty(i.artwork_id, i.size, i.quantity - 1, i.frame_color, i.material)} className="w-8 h-8 border border-ink-20 flex items-center justify-center hover:bg-ink hover:text-[color:var(--brand-cream)]"><Minus size={14}/></button>
                          <span className="w-8 text-center font-mono-brand" data-testid={`qty-${i.artwork_id}`}>{i.quantity}</span>
                          <button onClick={() => setQty(i.artwork_id, i.size, i.quantity + 1, i.frame_color, i.material)} className="w-8 h-8 border border-ink-20 flex items-center justify-center hover:bg-ink hover:text-[color:var(--brand-cream)]"><Plus size={14}/></button>
                        </div>
                      )}
                      <button onClick={() => { remove(i.artwork_id, i.size, i.frame_color, i.material); setQuote(null); }} data-testid={`remove-${i.artwork_id}-${i.size}-${i.frame_color || "unframed"}-${i.material || "poster"}`} className="text-sm text-mute hover:text-red-800 flex items-center gap-1">
                        <Trash2 size={14}/> Remove
                      </button>
                    </div>
                  </div>
                  <div className="text-right font-serif text-2xl text-ink">
                    {money(lineTotal(i))}
                  </div>
                </div>
              ))}
              <button onClick={() => { clear(); setQuote(null); }} className="text-sm text-mute hover:text-ink underline">Clear cart</button>

              {/* Shipping address */}
              <div className="mt-10 pt-8 border-t border-ink-10">
                <div className="overline text-mute mb-4">Shipping Address</div>
                <div className="grid md:grid-cols-2 gap-4">
                  <input required className="input-line" placeholder="First name" data-testid="addr-first-name" value={addr.first_name} onChange={e => { setAddr({ ...addr, first_name: e.target.value }); setQuote(null); }}/>
                  <input required className="input-line" placeholder="Last name" data-testid="addr-last-name" value={addr.last_name} onChange={e => { setAddr({ ...addr, last_name: e.target.value }); setQuote(null); }}/>
                  <input required type="email" className="input-line md:col-span-2" placeholder="Email" data-testid="addr-email" value={addr.email} onChange={e => setAddr({ ...addr, email: e.target.value })}/>
                  <input className="input-line md:col-span-2" placeholder="Phone (optional)" value={addr.phone} onChange={e => setAddr({ ...addr, phone: e.target.value })}/>
                  <input required className="input-line md:col-span-2" placeholder="Address line 1" data-testid="addr-line1" value={addr.address_line1} onChange={e => { setAddr({ ...addr, address_line1: e.target.value }); setQuote(null); }}/>
                  <input className="input-line md:col-span-2" placeholder="Apartment, suite, etc. (optional)" value={addr.address_line2} onChange={e => setAddr({ ...addr, address_line2: e.target.value })}/>
                  <input required className="input-line" placeholder="City" data-testid="addr-city" value={addr.city} onChange={e => { setAddr({ ...addr, city: e.target.value }); setQuote(null); }}/>
                  <input className="input-line" placeholder="State / Province" data-testid="addr-state" value={addr.state} onChange={e => { setAddr({ ...addr, state: e.target.value }); setQuote(null); }}/>
                  <input required className="input-line" placeholder="Postal code" data-testid="addr-postal" value={addr.postal_code} onChange={e => { setAddr({ ...addr, postal_code: e.target.value }); setQuote(null); }}/>
                  <select required className="input-line bg-transparent" data-testid="addr-country" value={addr.country} onChange={e => { setAddr({ ...addr, country: e.target.value }); setQuote(null); }}>
                    {COUNTRIES.map(([c, n]) => <option key={c} value={c}>{n}</option>)}
                  </select>
                </div>
              </div>
            </div>

            <aside className="lg:col-span-1">
              <div className="bg-cream-soft p-8 sticky top-24">
                <div className="overline text-mute mb-6">Order Summary</div>
                <div className="flex justify-between text-sm mb-3">
                  <span>Subtotal</span><span data-testid="cart-subtotal">{money(subtotal)}</span>
                </div>
                <div className="flex justify-between text-sm mb-6">
                  <span>Shipping</span>
                  <span data-testid="cart-shipping">
                    {quote ? (quote.shipping_amount === 0 ? "Free" : money(quote.shipping_amount)) : (hasPrints ? "—" : "Free")}
                  </span>
                </div>
                {quote && quote.min_days && (
                  <div className="text-xs text-mute mb-6 -mt-3 text-right">
                    Est. {quote.min_days}–{quote.max_days} business days
                  </div>
                )}
                <div className="border-t border-ink-10 pt-4 flex justify-between font-serif text-2xl text-ink mb-6">
                  <span>Total</span><span data-testid="cart-total">{money(grandTotal)}</span>
                </div>

                {hasPrints && (
                  <button
                    onClick={getQuote}
                    disabled={quoting || !addressComplete}
                    data-testid="calc-shipping-btn"
                    className="btn-outline w-full mb-3 justify-center"
                  >
                    <Truck size={14}/> {quoting ? "Calculating…" : quote ? "Recalculate Shipping" : "Calculate Shipping"}
                  </button>
                )}

                <button
                  onClick={checkout}
                  disabled={checking || !addressComplete || (hasPrints && !quote)}
                  data-testid="cart-checkout"
                  className="btn-ink w-full"
                >
                  {checking ? "Redirecting…" : `Checkout · ${money(grandTotal)}`}
                </button>
                <p className="text-xs text-mute text-center mt-3">
                  {hasPrints
                    ? "Prints ship worldwide via Gelato · Real-time rates"
                    : "Original artwork · Shipping arranged after order"}
                </p>
                <p className="text-xs text-mute text-center mt-1">Secure checkout via Stripe — card, Apple Pay, Google Pay, Cash App Pay, or Link. Charged in USD.</p>
              </div>
            </aside>
          </div>
        )}
      </section>
    </PublicShell>
  );
}
